import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Grid,
  Box,
  Chip,
} from '@mui/material';
import {
  People,
  Business,
  Folder,
  Send,
  WbSunny,
  Brightness3,
  WbTwilight,
  InsertDriveFile,
} from '@mui/icons-material';
import { useAuth } from '../../../hooks/useAuth';
import { usePermissions } from '../../../hooks/usePermissions';
import StatCard from '../components/StatCard';
import QuickAccessWidget from '../components/QuickAccessWidget';
import LoadingLogo from '../../../components/LoadingLogo';
import { dashboardService } from '../services/dashboardService';

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour >= 6 && hour < 12) return { text: 'Buenos días', icon: WbSunny };
  if (hour >= 12 && hour < 19) return { text: 'Buenas tardes', icon: WbTwilight };
  return { text: 'Buenas noches', icon: Brightness3 };
};

const getFormattedDate = () => {
  return new Date().toLocaleDateString('es-MX', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const DashboardPage = () => {
  const { user, isOwner } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  // Check if user has permission
  const hasPermission = (permission) => {
    if (!permission) return true;
    if (!user?.allPermissions) return false;
    if (user.role === 'SUPER_ADMIN') return true;
    return user.allPermissions.includes(permission);
  };

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        const statsData = await dashboardService.getStats();
        setStats(statsData);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const quickActions = [
    {
      title: 'Usuarios',
      icon: <People />,
      color: '#288FC6',
      path: '/users',
      permission: 'user.view',
    },
    {
      title: 'Empresas',
      icon: <Business />,
      color: '#02518C',
      path: isOwner ? '/companies' : `/companies/${user?.companyId}`,
      permission: 'company.view',
    },
    {
      title: 'Expedientes',
      icon: <Folder />,
      color: '#153250',
      path: '/proceedings',
      permission: 'proceeding.view',
    },
    {
      title: 'Correspondencia',
      icon: <Send />,
      color: '#1B6FA8',
      path: '/correspondences',
      permission: 'correspondence.view',
    },
  ].filter(action => hasPermission(action.permission));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <LoadingLogo size={120} />
      </Box>
    );
  }

  const greeting = getGreeting();
  const GreetingIcon = greeting.icon;

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          p: 3.5,
          borderRadius: '20px',
          background: 'linear-gradient(135deg, #EBF5FB 0%, #EDF2F8 100%)',
          border: '1px solid rgba(226,232,240,0.7)',
          boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <GreetingIcon sx={{ fontSize: 20, color: '#288FC6' }} />
            <Typography
              sx={{
                fontSize: '0.85rem',
                fontWeight: 600,
                color: '#64748B',
                letterSpacing: '0.02em',
              }}
            >
              {greeting.text}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: '1.75rem',
              fontWeight: 800,
              color: '#0F172A',
              letterSpacing: '-0.03em',
              lineHeight: 1.2,
            }}
          >
            {user?.name?.split(' ')[0] || 'Usuario'}
          </Typography>
          <Typography
            sx={{
              fontSize: '0.8125rem',
              color: '#94A3B8',
              mt: 0.5,
              fontWeight: 500,
              textTransform: 'capitalize',
            }}
          >
            {getFormattedDate()}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip
            label="Sistema activo"
            size="small"
            sx={{
              bgcolor: '#DCFCE7',
              color: '#15803D',
              fontWeight: 600,
              fontSize: '0.72rem',
              height: 26,
              '& .MuiChip-label': { px: 1.5 },
            }}
          />
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Usuarios"
            value={stats?.users.total.toLocaleString()}
            icon={<People />}
            color="#288FC6"
            subtitle={stats?.users.subtitle}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Empresas"
            value={stats?.companies.total}
            icon={<Business />}
            color="#02518C"
            subtitle={stats?.companies.subtitle}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Expedientes"
            value={stats?.proceedings.total}
            icon={<Folder />}
            color="#153250"
            subtitle={stats?.proceedings.subtitle}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Correspondencia"
            value={stats?.correspondence.total}
            icon={<Send />}
            color="#1B6FA8"
            subtitle={stats?.correspondence.subtitle}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Documentos"
            value={stats?.documents?.total ?? 0}
            icon={<InsertDriveFile />}
            color="#0E7490"
            subtitle={stats?.documents?.subtitle}
          />
        </Grid>
      </Grid>

      {/* Quick Access */}
      <QuickAccessWidget actions={quickActions} />
    </Box>
  );
};

export default DashboardPage;
